import { Color, FontFamilies, PDSTypeStyle, Typography } from '@persona-id/design-tokens';

interface PrimaryColors {
  black: Color;
  white: Color;
  brand: Color;
  brand800: Color;
  brand700: Color;
  brand600: Color;
  brand500: Color;
  brand400: Color;
  brand300: Color;
  brand200: Color;
  brand100: Color;
}

export interface TextColors {
  primary: Color;
  secondary: Color;
  tertiary: Color;
  brand: Color;
  inverse: Color;
  disabled: Color;
}

export interface BackgroundColors {
  primary: Color;
  secondary: Color;
  active: Color;
  highlight: Color;
  disabled: Color;
}

export type Variant = 'basic' | 'primary' | 'inverse' | 'danger' | 'caution' | 'success';

interface VariantPalette {
  /**
   * Activated color. Mousedowns for buttons, hover for dropdowns, etc.
   */
  active: Color;
  /**
   * Passive color when not interacted.
   */
  default: Color;
  /**
   * Focus state, borders, etc. Generally lighter than the default color.
   */
  highlight: Color;
  /**
   * Alternative highlight color.
   */
  highlightLight: Color;
  /**
   * Alternative highlight color.
   */
  highlightDark: Color;

  /**
   * Disabled background
   */
  disabled: Color;

  text: Color;
  textDisabled: Color;
}

interface ColorPalette {
  primary: PrimaryColors;
  text: TextColors;
  variants: Record<Variant, VariantPalette>;
  background: BackgroundColors;
  border: {
    primary: Color;
    secondary: Color;
    active: Color;
    input: Color;
    disabled: Color;
  };
}

interface TextSettings {
  fontFamily: {
    base: string;
    mono: string;
  };
  display: {
    lg: PDSTypeStyle;
    md: PDSTypeStyle;
    sm: PDSTypeStyle;
  };
  heading: {
    lg: PDSTypeStyle;
    md: PDSTypeStyle;
    sm: PDSTypeStyle;
  };
  paragraph: {
    lg: PDSTypeStyle;
    lgBold: PDSTypeStyle;
    lgLight: PDSTypeStyle;
    md: PDSTypeStyle;
    mdBold: PDSTypeStyle;
    mdLight: PDSTypeStyle;
    mdLink: PDSTypeStyle;
    sm: PDSTypeStyle;
    smLink: PDSTypeStyle;
    xs: PDSTypeStyle;
  };
}

// SHARED ELEMENT VARIABLES
// For inputs, selects, and input-like components
interface ElementSettings {
  borderRadius: string;
  height: {
    xl: string;
    lg: string;
    md: string;
    sm: string;
    xs: string;
  };
  width: {
    md: string;
  };
}

// COMPONENT SPECIFIC VARIABLES
interface ButtonSettings {
  borderRadius: string;
  height: {
    md: string;
    sm: string;
  };
}

interface CardSettings {
  borderRadius: string;
}

interface ModalSettings {
  borderRadius: {
    xl: string;
    lg: string;
    md: string;
  };
  width: {
    xl: string;
    lg: string;
    md: string;
  };
}

export interface PersonaTheme {
  color: ColorPalette;
  text: TextSettings;
  element: ElementSettings;
  card: CardSettings;
  button: ButtonSettings;
  modal: ModalSettings;
}

const primary: PrimaryColors = {
  black: Color.PrimaryBlack,
  white: Color.PrimaryWhite,
  brand: Color.PrimaryPurple,
  brand800: Color.CorePurple800,
  brand700: Color.CorePurple700,
  brand600: Color.CorePurple600,
  brand500: Color.CorePurple500,
  brand400: Color.CorePurple400,
  brand300: Color.CorePurple300,
  brand200: Color.CorePurple200,
  brand100: Color.CorePurple100,
};

// for active state, use variant!
const lightText: TextColors = {
  primary: Color.TextPrimary,
  secondary: Color.TextSecondary,
  tertiary: Color.CoreSlate800,
  brand: primary.brand,
  inverse: Color.TextInverse,
  disabled: Color.TextDisabled,
};

// for active state, use variant!
const darkText: TextColors = {
  primary: Color.TextInverse,
  secondary: Color.CoreSlate500,
  tertiary: Color.CoreSlate800,
  brand: primary.brand,
  inverse: Color.TextPrimary,
  disabled: Color.TextDisabled,
};

const lightColorPalette: ColorPalette = {
  primary,
  text: lightText,
  background: {
    primary: Color.BackgroundPrimary,
    secondary: Color.BackgroundSecondary,
    active: primary.brand,
    highlight: primary.brand100,
    disabled: Color.BackgroundInputDisabled,
  },
  variants: {
    basic: {
      active: Color.BackgroundButtonDefaultActive,
      default: Color.BackgroundButtonDefault,
      highlight: Color.CoreSlate400,
      highlightLight: Color.CoreSlate400,
      highlightDark: Color.CoreSlate700,
      disabled: Color.CoreSlate100,
      text: lightText.primary,
      textDisabled: lightText.disabled,
    },
    primary: {
      active: Color.BackgroundButtonPrimaryActive,
      default: Color.BackgroundButtonPrimary,
      highlight: primary.brand200,
      highlightLight: primary.brand100,
      highlightDark: primary.brand200,
      disabled: Color.BackgroundButtonPrimaryDisabled,
      text: lightText.inverse,
      textDisabled: lightText.inverse,
    },
    inverse: {
      active: primary.brand100,
      default: primary.white,
      highlight: primary.brand200,
      highlightLight: primary.brand100,
      highlightDark: primary.brand700,
      disabled: primary.white,
      text: lightText.primary,
      textDisabled: lightText.disabled,
    },
    danger: {
      active: Color.BackgroundButtonDangerActive,
      default: Color.BackgroundButtonDanger,
      highlight: Color.CoreRed200,
      highlightLight: Color.CoreRed100,
      highlightDark: Color.CoreRed200,
      disabled: Color.BackgroundButtonDangerDisabled,
      text: lightText.inverse,
      textDisabled: lightText.inverse,
    },
    caution: {
      active: Color.CoreYellow900,
      default: Color.CoreYellow800,
      highlight: Color.CoreYellow200,
      highlightLight: Color.CoreYellow100,
      highlightDark: Color.CoreYellow200,
      disabled: Color.CoreYellow100,
      text: lightText.inverse,
      textDisabled: lightText.inverse,
    },
    success: {
      active: Color.CoreGreen900,
      default: Color.CoreGreen800,
      highlight: Color.CoreGreen200,
      highlightLight: Color.CoreGreen100,
      highlightDark: Color.CoreGreen200,
      disabled: Color.CoreGreen100,
      text: lightText.inverse,
      textDisabled: lightText.inverse,
    },
  },
  border: {
    primary: Color.BorderHairline,
    secondary: Color.BorderDivider,
    active: Color.BorderInputActive,
    input: Color.BorderInputDefault,
    disabled: Color.BorderInputDisabled,
  },
};

const darkColorPalette: ColorPalette = {
  primary,
  text: darkText,
  background: {
    primary: Color.CoreSlate1000,
    secondary: Color.CoreSlate50,
    active: primary.brand,
    highlight: Color.PrimaryWhite,
    disabled: Color.CoreSlate300,
  },
  variants: {
    basic: {
      active: Color.BackgroundButtonDefaultActive,
      default: Color.BackgroundButtonDefault,
      highlight: Color.CoreSlate400,
      highlightLight: Color.CoreSlate400,
      highlightDark: Color.CoreSlate900,
      disabled: Color.CoreSlate100,
      text: darkText.primary,
      textDisabled: darkText.disabled,
    },
    primary: {
      active: Color.CoreSlate900,
      default: Color.CoreSlate1000,
      highlight: primary.brand200,
      highlightLight: primary.brand100,
      highlightDark: primary.brand200,
      disabled: Color.BackgroundButtonPrimaryDisabled,
      text: primary.white,
      textDisabled: darkText.inverse,
    },
    inverse: {
      active: primary.brand100,
      default: primary.white,
      highlight: primary.brand200,
      highlightLight: primary.brand100,
      highlightDark: primary.brand700,
      disabled: primary.white,
      text: darkText.inverse,
      textDisabled: darkText.disabled,
    },
    danger: {
      active: Color.BackgroundButtonDangerActive,
      default: Color.BackgroundButtonDanger,
      highlight: Color.CoreRed300,
      highlightLight: Color.CoreRed100,
      highlightDark: Color.CoreRed300,
      disabled: Color.BackgroundButtonDangerDisabled,
      text: darkText.inverse,
      textDisabled: darkText.inverse,
    },
    caution: {
      active: Color.CoreYellow900,
      default: Color.CoreYellow800,
      highlight: Color.CoreYellow200,
      highlightLight: Color.CoreYellow100,
      highlightDark: Color.CoreYellow200,
      disabled: Color.CoreYellow100,
      text: darkText.inverse,
      textDisabled: darkText.inverse,
    },
    success: {
      active: Color.CoreGreen900,
      default: Color.CoreGreen800,
      highlight: Color.CoreGreen200,
      highlightLight: Color.CoreGreen100,
      highlightDark: Color.CoreGreen200,
      disabled: Color.CoreGreen100,
      text: darkText.inverse,
      textDisabled: darkText.inverse,
    },
  },
  border: {
    primary: Color.CoreSlate900,
    secondary: Color.BorderDivider,
    active: Color.BorderInputActive,
    input: Color.BorderInputDefault,
    disabled: Color.BorderInputDisabled,
  },
};

const defaultTextSettings: TextSettings = {
  fontFamily: {
    base: FontFamilies.Primary,
    mono: FontFamilies.Monospace,
  },
  display: {
    lg: Typography.DisplayLarge,
    md: Typography.DisplayMedium,
    sm: Typography.DisplaySmall,
  },
  heading: {
    lg: Typography.HeadingLarge,
    md: Typography.HeadingMedium,
    sm: Typography.HeadingSmall,
  },
  paragraph: {
    lg: Typography.TextLarge,
    lgBold: Typography.TextLargeBold,
    lgLight: Typography.TextLargeLight,
    md: Typography.TextMedium,
    mdBold: Typography.TextMediumBold,
    mdLight: Typography.TextMediumLight,
    mdLink: Typography.TextMediumLink,
    sm: Typography.TextSmall,
    smLink: Typography.TextSmallLink,
    xs: Typography.TextExtraSmall,
  },
};

const textSettingsWithMarketingFont: TextSettings = {
  ...defaultTextSettings,
  fontFamily: {
    base: FontFamilies.Primary,
    mono: FontFamilies.Monospace,
  },
};

const defaultElementSettings: ElementSettings = {
  borderRadius: '4px',
  height: {
    xl: '128px',
    lg: '56px',
    md: '48px',
    sm: '40px',
    xs: '32px',
  },
  width: {
    md: '228px',
  },
};

const defaultButtonSettings: ButtonSettings = {
  borderRadius: '4px',
  height: {
    md: '32px',
    sm: '24px',
  },
};

const defaultCardSettings: CardSettings = {
  borderRadius: '8px',
};

const defaultModalSettings: ModalSettings = {
  borderRadius: {
    xl: '8px',
    lg: '4px',
    md: '4px',
  },
  width: {
    xl: '1020px',
    lg: '720px',
    md: '520px',
  },
};

const defaultTheme: PersonaTheme = {
  color: lightColorPalette,
  text: defaultTextSettings,
  element: defaultElementSettings,
  button: defaultButtonSettings,
  card: defaultCardSettings,
  modal: defaultModalSettings,
};

export const darkTheme: PersonaTheme = {
  color: darkColorPalette,
  text: defaultTextSettings,
  element: defaultElementSettings,
  button: defaultButtonSettings,
  card: defaultCardSettings,
  modal: defaultModalSettings,
};

export const marketingFontTheme: PersonaTheme = {
  color: lightColorPalette,
  text: textSettingsWithMarketingFont,
  element: defaultElementSettings,
  button: defaultButtonSettings,
  card: defaultCardSettings,
  modal: defaultModalSettings,
};

export default defaultTheme;
